/**
 * Hero Carousel - Full-screen slide carousel using Slyde
 * Supports autoplay with progress bars and slide index navigation
 */

(function() {
  const { Slyde } = window.Slyde || {};

  if (!Slyde) {
    console.error('Slyde library not loaded');
    return;
  }

  class HeroCarousel extends HTMLElement {
    constructor() {
      super();
      this._slyde = null;
      this._autoplayTimer = null;
      this._bars = [];
      this._totalSlides = 0;
    }

    connectedCallback() {
      requestAnimationFrame(() => this._init());
    }

    disconnectedCallback() {
      this._stopAutoplay();
      if (this._slyde) {
        try { this._slyde.destroy(); } catch (e) {}
        this._slyde = null;
      }
    }

    _init() {
      const wrapper = this.querySelector('.slyde-wrapper');
      if (!wrapper) return;

      const slides = wrapper.querySelectorAll('.slyde-slide');
      if (slides.length <= 1) return;

      this._totalSlides = slides.length;
      this._bars = Array.from(this.querySelectorAll('.hero__nav-bar'));

      this._autoplay = this.dataset.autoplay === 'true';
      this._speed = parseInt(this.dataset.speed, 10) || 5;

      // Set CSS custom property for progress bar animation
      this.style.setProperty('--hero-autoplay-speed', this._speed + 's');

      this._slyde = new Slyde(this, {
        loop: true,
        navigation: false,
        pagination: false,
        thumbnails: false,
        video: false,
        slidesPerView: 1,
        spaceBetween: 0,
        freeMode: false
      });

      this._slyde.on('slideChange', () => this._onSlideChange());

      // Bar click navigation
      this._bars.forEach((bar) => {
        bar.addEventListener('click', () => {
          const idx = parseInt(bar.dataset.index, 10);
          this._slyde.goTo(idx);
        });
      });

      // Autoplay
      if (this._autoplay) {
        this._startAutoplay(this._speed);
      }
    }

    _onSlideChange() {
      const rawIdx = this._slyde.currentIndex;
      const idx = ((rawIdx % this._totalSlides) + this._totalSlides) % this._totalSlides;

      // Reset autoplay timer on any slide change
      if (this._autoplay) {
        this._restartAutoplay(this._speed);
      }

      // Update active bar
      this._bars.forEach((bar, i) => {
        const isActive = i === idx;
        bar.classList.toggle('is-active', isActive);
        // Reset fill animation by re-triggering reflow
        if (isActive) {
          const fill = bar.querySelector('.hero__nav-bar-fill');
          if (fill) {
            fill.style.animation = 'none';
            fill.offsetHeight; // force reflow
            fill.style.animation = '';
          }
        }
      });
    }

    _startAutoplay(speed) {
      this._stopAutoplay();
      this._autoplayTimer = setInterval(() => {
        this._slyde?.next();
      }, speed * 1000);
    }

    _restartAutoplay(speed) {
      this._stopAutoplay();
      this._startAutoplay(speed);
    }

    _stopAutoplay() {
      if (this._autoplayTimer) {
        clearInterval(this._autoplayTimer);
        this._autoplayTimer = null;
      }
    }

    // Public API
    next() { this._slyde?.next(); }
    prev() { this._slyde?.prev(); }
    goTo(index) { this._slyde?.goTo(index); }
    get currentIndex() { return this._slyde?.currentIndex || 0; }
  }

  if (!customElements.get('hero-carousel')) {
    customElements.define('hero-carousel', HeroCarousel);
  }
})();
